import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { cardRestShadow, cardHoverShadow } from '../theme/styleHelpers';

type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  mode: ThemeMode;
  effectiveMode: 'light' | 'dark';
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'auto',
  effectiveMode: 'light',
  toggleTheme: () => {},
  setThemeMode: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    // Initialize from localStorage
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode && ['light', 'dark', 'auto'].includes(savedMode)) {
      return savedMode as ThemeMode;
    }
    return 'auto';
  });
  const [effectiveMode, setEffectiveMode] = useState<'light' | 'dark'>('light');
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Calculate effective mode based on mode setting and system preference
  useEffect(() => {
    let effective: 'light' | 'dark';
    if (mode === 'auto') {
      effective = systemPrefersDark ? 'dark' : 'light';
    } else {
      effective = mode as 'light' | 'dark';
    }
    setEffectiveMode(effective);
    
    // Store in localStorage for persistence
    localStorage.setItem('themeMode', mode);
    localStorage.setItem('effectiveThemeMode', effective);
  }, [mode, systemPrefersDark]);

  const toggleTheme = useCallback(() => {
    setMode(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const setThemeMode = useCallback((newMode: ThemeMode) => {
    setMode(newMode);
  }, []);

  const theme = React.useMemo(
    () => {
      const isLight = effectiveMode === 'light';
      return createTheme({
        palette: {
          mode: effectiveMode,
          primary: isLight
            ? { main: '#1B8FB8', light: '#22B5DC', dark: '#1A4F75', contrastText: '#fff' }
            : { main: '#22B5DC', light: '#4FC9E6', dark: '#1B8FB8', contrastText: '#0E3A5F' },
          secondary: { main: '#0E3A5F', light: '#1A4F75', dark: '#0A2540' },
          brand: {
            cyan: '#22B5DC',
            cyanSoft: '#4FC9E6',
            teal: '#1B8FB8',
            blue: '#1A4F75',
            navy: '#0E3A5F',
            topbarDark: '#0B1828',
          },
          fileTints: {
            pdf: '#FCE7DE',
            doc: '#DDE7F0',
            html: '#E6DDF5',
            ppt: '#FBE7C2',
            audio: '#D6F0E2',
            img: '#F4DEEE',
            video: '#FBD9DB',
            view: '#EDE9FE',
            archive: isLight ? '#EAE3D6' : '#3A322A',
          },
          fileDots: {
            pdf: '#DC2626',
            doc: '#6B7B8C',
            html: '#7C5CE0',
            ppt: '#D97706',
            audio: '#059669',
            img: '#DB2777',
            video: '#DC2626',
            view: '#7C3AED',
            archive: isLight ? '#92753E' : '#C8A062',
          },
          ...(isLight
            ? {
                background: { default: '#f8fafc', paper: '#ffffff' },
                text: { primary: '#0f172a', secondary: '#64748b' },
                divider: 'rgba(15,23,42,0.08)',
                action: {
                  active: 'rgba(15,23,42,0.6)',
                  hover: 'rgba(15,23,42,0.04)',
                  selected: 'rgba(27,143,184,0.10)',
                  disabled: 'rgba(15,23,42,0.26)',
                  disabledBackground: 'rgba(15,23,42,0.08)',
                },
              }
            : {
                background: { default: '#0A1422', paper: '#0F1E33' },
                text: { primary: '#ffffff', secondary: 'rgba(255,255,255,0.55)' },
                divider: 'rgba(255,255,255,0.07)',
                action: {
                  active: 'rgba(255,255,255,0.9)',
                  hover: 'rgba(255,255,255,0.06)',
                  selected: 'rgba(34,181,220,0.16)',
                  disabled: 'rgba(255,255,255,0.3)',
                  disabledBackground: 'rgba(255,255,255,0.08)',
                },
              }),
        },
        shape: { borderRadius: 12 },
        typography: {
          fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
          h1: { fontWeight: 800, letterSpacing: '-0.025em' },
          h2: { fontWeight: 700, letterSpacing: '-0.02em' },
          h3: { fontWeight: 700 },
          button: { textTransform: 'none', fontWeight: 600 },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: { fontVariantNumeric: 'normal' },
              '.tabular': { fontVariantNumeric: 'tabular-nums' },
            },
          },
          MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
              root: { borderRadius: 10 },
              containedPrimary: ({ theme }) => ({
                background: theme.palette.brand.teal,
                color: '#fff',
                '&:hover': { background: theme.palette.brand.blue },
              }),
            },
          },
          MuiCard: {
            styleOverrides: {
              root: ({ theme }) => ({
                borderRadius: 14,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: cardRestShadow(theme),
                transition: 'transform 0.25s cubic-bezier(.2,.8,.2,1), box-shadow 0.25s ease, border-color 0.25s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: cardHoverShadow(theme),
                  borderColor: isLight ? theme.palette.divider : alpha(theme.palette.brand.cyan, 0.25),
                },
              }),
            },
          },
          MuiPaper: {
            styleOverrides: {
              rounded: { borderRadius: 12 },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: ({ theme }) => ({
                fontWeight: 700,
                fontSize: '10.5px',
                letterSpacing: '0.02em',
                height: 22,
                textTransform: 'lowercase',
                '&[data-status="public"]': {
                  background: theme.palette.mode === 'light' ? 'rgba(16,185,129,.12)' : 'rgba(16,185,129,.18)',
                  color: theme.palette.mode === 'light' ? '#047857' : '#34D399',
                },
                '&[data-status="draft"]': {
                  background: theme.palette.mode === 'light' ? 'rgba(245,158,11,.14)' : 'rgba(245,158,11,.18)',
                  color: theme.palette.mode === 'light' ? '#B45309' : '#FBBF24',
                },
                '&[data-status="private"]': {
                  background: theme.palette.mode === 'light' ? 'rgba(34,181,220,.14)' : 'rgba(34,181,220,.18)',
                  color: theme.palette.mode === 'light' ? theme.palette.brand.teal : theme.palette.brand.cyan,
                },
                '&[data-status="archived"]': {
                  background: theme.palette.action.hover,
                  color: theme.palette.text.secondary,
                },
              }),
            },
          },
          MuiTabs: {
            styleOverrides: {
              root: ({ theme }) => ({
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 12,
                padding: 4,
                minHeight: 'auto',
                width: 'fit-content',
                boxShadow: '0 1px 2px rgba(15,23,42,.03)',
              }),
              indicator: { display: 'none' },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: ({ theme }) => ({
                padding: '8px 16px',
                minHeight: 'auto',
                borderRadius: 9,
                fontSize: 12.5,
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'none',
                '&.Mui-selected': {
                  background: theme.palette.brand.teal,
                  color: '#fff',
                },
              }),
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: ({ theme }) => ({
                backgroundColor: isLight ? '#fff' : theme.palette.brand.topbarDark,
                color: theme.palette.text.primary,
                boxShadow: 'none',
                borderBottom: `1px solid ${isLight ? 'rgba(15,23,42,.08)' : 'rgba(255,255,255,.06)'}`,
              }),
            },
          },
        },
      });
    },
    [effectiveMode]
  );

  const contextValue = useMemo(
    () => ({ mode, effectiveMode, toggleTheme, setThemeMode }),
    [mode, effectiveMode, toggleTheme, setThemeMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
